
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { toast } from '@/components/ui/toast'
import { useInvites } from '@/hooks/useInvites'
import { InviteViaEmailDialog } from '../dialogs/InviteViaEmailDialog'
import { InviteSuccessDialog } from '../dialogs/InviteSuccessDialog'

export function InvitesSection() {
  const { dashboard, loading, error, createLink, sendEmailInvites, refresh } = useInvites()
  const [creatingLink, setCreatingLink] = useState(false)
  const [inviteViaEmailDialogOpen, setInviteViaEmailDialogOpen] = useState(false)
  const [inviteSuccessDialogOpen, setInviteSuccessDialogOpen] = useState(false)
  const [inviteSuccessCount, setInviteSuccessCount] = useState(0)

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url)
    toast.success('Link copied to clipboard')
  }

  const handleCreateNewLink = async () => {
    setCreatingLink(true)
    await createLink()
    setCreatingLink(false)
  }

  const handleInviteViaEmail = () => {
    setInviteViaEmailDialogOpen(true)
  }

  const handleEmailInviteSuccess = (count: number) => {
    setInviteSuccessCount(count)
    setInviteSuccessDialogOpen(true)
  }

  const inviteLinks = dashboard?.links ?? []
  const referrals = dashboard?.referrals ?? []

  return (
    <div className="p-6 flex flex-col gap-6">
      <div>
        <h3 className="text-base font-semibold">Invites</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Share your invite links and track your referrals to earn rewards.
        </p>
      </div>

      <div className="space-y-6">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-px w-full" />
            <Skeleton className="h-10 w-40" />
            <Skeleton className="h-px w-full" />
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-48 w-full" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-4 py-8">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="lg" onClick={refresh}>
              Try again
            </Button>
          </div>
        ) : (
          <>
            <p className="text-sm font-medium text-foreground">Your invite links</p>

            <div className="space-y-3">
              {inviteLinks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No invite links yet. Create one to start inviting.</p>
              ) : (
                inviteLinks.map((link) => (
                  <div key={link.id} className="flex items-center gap-2">
                    <Input
                      value={link.url}
                      readOnly
                      className="flex-1"
                    />
                    <Button
                      variant="outline"
                      size="lg"
                      onClick={() => handleCopyLink(link.url)}
                    >
                      <Icon name="copy" className="h-4 w-4 mr-2" />
                      Copy
                    </Button>
                  </div>
                ))
              )}
            </div>

            <Button
              variant="outline"
              size="lg"
              className="w-full"
              onClick={handleCreateNewLink}
              disabled={creatingLink}
            >
              {creatingLink ? (
                <Icon name="loader-2" className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Icon name="plus" className="h-4 w-4 mr-2" />
              )}
              {creatingLink ? 'Creating...' : 'Create new link'}
            </Button>

            <Separator />

            <Button
              variant="outline"
              size="lg"
              onClick={handleInviteViaEmail}
            >
              <Icon name="mail" className="h-4 w-4 mr-2" />
              Invite via Email
            </Button>

            <Separator />

            <p className="text-sm font-medium">Referrals</p>

            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invite code</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead className="text-right">Earned</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                        No referrals yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    referrals.map((referral) => (
                      <TableRow key={referral.id}>
                        <TableCell className="font-medium">{referral.inviteCode}</TableCell>
                        <TableCell>{referral.source}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {referral.earnings.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">
                          {/* The dashboard only returns accepted invites; anything else is shown as-is */}
                          {referral.status === 'ACCEPTED' ? (
                            <Badge variant="default" className="font-normal">
                              Accepted
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="font-normal">
                              {referral.status.toLowerCase()}
                            </Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {inviteViaEmailDialogOpen && (
        <InviteViaEmailDialog
          open={inviteViaEmailDialogOpen}
          onOpenChange={setInviteViaEmailDialogOpen}
          onSuccess={handleEmailInviteSuccess}
          sendEmailInvites={sendEmailInvites}
        />
      )}

      {inviteSuccessDialogOpen && (
        <InviteSuccessDialog
          open={inviteSuccessDialogOpen}
          onOpenChange={setInviteSuccessDialogOpen}
          count={inviteSuccessCount}
          type="email"
        />
      )}
    </div>
  )
}
